import { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
    syncPaginationStateWithUrl,
    updatePaginationUrl,
} from "../helpers/TablePageUtils";
import { CommonListPageState } from "../helpers/TablePageUtils";

/**
 * Настройки синхронизации состояния пагинации со строкой URL.
 * @interface UrlPaginationOptions
 * @property {string} [defaultSortColumn] - Колонка для сортировки по умолчанию.
 * @property {number} [defaultLimit=10] - Количество элементов на странице по умолчанию.
 * @property {string} [searchParam='search'] - Имя query‑параметра в URL для поискового запроса.
 * @property {string} [sortParam='sort'] - Имя query‑параметра в URL для колонки сортировки.
 * @property {string} [orderParam='order'] - Имя query‑параметра в URL для направления сортировки.
 * @property {string} [customParams=['specialGroupsIdsFilter']] - Массив кастомных фильтров в URL.
 */
export interface UrlPaginationOptions {
    defaultSortColumn?: string;
    defaultLimit?: number;
    searchParam?: string;
    sortParam?: string;
    orderParam?: string;
    customParams?: string[];
}

/**
 * Возвращаемый тип хука useUrlPagination.
 * @template T - Тип состояния пагинации, расширяющий CommonListPageState.
 * @interface UseUrlPaginationReturn
 * @property {T} state - Текущее состояние пагинации (page, limit, sortColumn, searchValue и т.д.).
 * @property {React.Dispatch<React.SetStateAction<T>>} setState - Сеттер состояния, совместимый с useState.
 * @property {(additionalFilters?: Record<string, string | undefined>) => void} updateUrl - Функция для обновления URL на основе текущего состояния и дополнительных фильтров.
 */
export interface UseUrlPaginationReturn<T extends CommonListPageState> {
    state: T;
    setState: React.Dispatch<React.SetStateAction<T>>;
    updateUrl: (additionalFilters?: Record<string, string | undefined>) => void;
}

// Получение значения по вложенному пути (например "filters.deptId")
const getNestedValue = (obj: any, path: string): any => {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
};

// Установка значения по вложенному пути
const setNestedValue = (obj: any, path: string, value: any): any => {
    const keys = path.split('.');
    const result = { ...obj };
    let current = result;

    for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        current[key] = { ...current[key] };
        current = current[key];
    }

    current[keys[keys.length - 1]] = value;
    return result;
};

// Глубокое слияние объектов
const deepMerge = (target: any, source: any): any => {
    const result = { ...target };
    for (const key of Object.keys(source)) {
        if (
            source[key] !== null &&
            typeof source[key] === 'object' &&
            !Array.isArray(source[key]) &&
            !(source[key] instanceof Date)
        ) {
            result[key] = deepMerge(result[key] || {}, source[key]);
        } else {
            result[key] = source[key];
        }
    }
    return result;
};

/**
 * Хук для синхронизации состояния пагинации (страница, лимит, сортировка, поиск) с URL.
 * Автоматически парсит параметры из URL при изменении location и обновляет URL при изменении состояния.
 * @template T — тип состояния, расширяющий CommonListPageState
 * @param initialState — начальное состояние (должно содержать page, limit, sortColumn, searchValue)
 * @param options — опции настройки параметров URL
 * @returns Объект с состоянием, setState и updateUrl
 */
export const useUrlPagination = <T extends CommonListPageState>(
    initialState: T,
    options: UrlPaginationOptions = {}
): UseUrlPaginationReturn<T> => {
    const {
        defaultSortColumn = "",
        defaultLimit = 10,
        searchParam = "search",
        sortParam = "sort",
        orderParam = "order",
        customParams = [],
    } = options;

    const location = useLocation();
    const history = useHistory();

    // Парсинг всего состояния из URL (базовое + кастомное)
    const parseStateFromUrl = (search: string): Partial<T> => {
        // Базовые параметры
        const baseState = syncPaginationStateWithUrl(search, defaultSortColumn);

        // Кастомные параметры (с поддержкой вложенных путей, например "filters.deptId")
        const urlParams = new URLSearchParams(search);
        let customState: Partial<T> = {};
        for (const param of customParams) {
            const value = urlParams.get(param);
            if (value !== null && value !== '') {
                let parsedValue: any;
                const dateMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
                if (dateMatch) {
                    const [, year, month, day] = dateMatch;
                    parsedValue = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                } else if (value.includes(',')) {
                    // Парсинг массивов (значения разделённые запятой)
                    parsedValue = value.split(',').filter(v => v !== '');
                } else if (value === 'true') {
                    parsedValue = true;
                } else if (value === 'false') {
                    parsedValue = false;
                } else {
                    parsedValue = value;
                }
                // Поддержка вложенных путей
                customState = setNestedValue(customState, param, parsedValue);
            }
        }

        return {
            ...baseState,
            ...customState,
        };
    };

    const [state, setState] = useState<T>(() => {
        const parsed = parseStateFromUrl(location.search);
        return deepMerge(initialState, parsed) as T;
    });

    // Синхронизация при изменении URL
    useEffect(() => {
        const parsed = parseStateFromUrl(location.search);
        setState(prev => deepMerge(prev, parsed) as T);
    }, [location.search, defaultSortColumn, customParams.join(',')]);

    // Обновление URL
    const updateUrl = () => {
        const baseParams = {
            page: state.page,
            limit: state.limit,
            sortColumn: state.sortColumn,
            searchValue: state.searchValue,
        };

        const additionalFilters: Record<string, string | undefined> = {};
        for (const param of customParams) {
            // Поддержка вложенных путей (например "filters.deptId")
            const value = getNestedValue(state, param);
            if (value != null) {
                // Если значение — Date → форматируем в YYYY-MM-DD
                if (value instanceof Date) {
                    const year = value.getFullYear();
                    const month = String(value.getMonth() + 1).padStart(2, '0');
                    const day = String(value.getDate()).padStart(2, '0');
                    additionalFilters[param] = `${year}-${month}-${day}`;
                } else if (Array.isArray(value)) {
                    // Если значение — массив → объединяем через запятую (пустые массивы игнорируем)
                    if (value.length > 0) {
                        additionalFilters[param] = value.join(',');
                    }
                } else if (value !== '') {
                    // Пустые строки игнорируем
                    additionalFilters[param] = String(value);
                }
            }
        }

        updatePaginationUrl(history, location, baseParams, additionalFilters);
    };

    return {
        state,
        setState,
        updateUrl,
    };
};